import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ChevronDown, ChevronRight, Settings2 } from 'lucide-react';
import { industries, platformModifiers, Platform } from './attributionData';
const fmt = (val: number) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
}).format(val);
const fmtNum = (val: number) => new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0
}).format(val);
const AnimatedNumber = ({
  value,
  format = 'number'
}: {
  value: number;
  format?: 'number' | 'currency' | 'ratio';
}) => {
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    const dur = 350;
    const frames = 16;
    const step = (value - display) / frames;
    let cur = display;
    let f = 0;
    const t = setInterval(() => {
      f++;
      cur += step;
      if (f >= frames) {
        clearInterval(t);
        setDisplay(value);
      } else setDisplay(cur);
    }, dur / frames);
    return () => clearInterval(t);
  }, [value]);
  if (format === 'currency') return <span data-id="element-621" data-mp="1olufrqi742lw">{fmt(display)}</span>;
  if (format === 'ratio') return <span data-id="element-622" data-mp="1ondx7kfl8jp9">{display.toFixed(1)}:1</span>;
  return <span data-id="element-623" data-mp="1amupkef1bfnm">{fmtNum(display)}</span>;
};
export function TrafficSourcesSlide() {
  const [industryKey, setIndustryKey] = useState('medspa');
  const industry = industries[industryKey];
  const [totalSpend, setTotalSpend] = useState(30000);
  const [toggleValue, setToggleValue] = useState(industry.toggle.default);
  const [includeReferrals, setIncludeReferrals] = useState(false);
  useEffect(() => {
    setToggleValue(industries[industryKey].toggle.default);
  }, [industryKey]);
  const m = useMemo(() => {
    let leads = 0,
      appts = 0,
      shows = 0,
      purchases = 0;
    (Object.keys(platformModifiers) as Platform[]).forEach(p => {
      const spend = totalSpend * (industry.distribution[p] / 100);
      const cpl = industry.cpl * platformModifiers[p].cplMult;
      const pl = spend / cpl;
      const pa = pl * industry.leadToAppt;
      const ps = pa * industry.apptToShow;
      const pp = ps * (industry.showToPurchase * platformModifiers[p].closeMult);
      leads += pl;
      appts += pa;
      shows += ps;
      purchases += pp;
    });
    const {
      ltvMultiplier = 1,
      ticketMultiplier = 1,
      marginBoost = 0,
      conversionMultiplier = 1
    } = industry.toggle.effect(toggleValue);
    // Apply conversion multiplier to funnel stages
    appts *= conversionMultiplier;
    shows *= conversionMultiplier;
    purchases *= conversionMultiplier;
    if (includeReferrals) purchases *= industry.referralMultiplier;
    const ticket = industry.firstVisitTicket * ticketMultiplier;
    const revFirst = Math.round(purchases * ticket / 100) * 100;
    const revGoBack = Math.round(purchases * industry.goBack12mo * ltvMultiplier / 100) * 100;
    const revLTV = Math.round(purchases * industry.ltv5yr * ltvMultiplier / 100) * 100;
    const margin = industry.margin + marginBoost;
    const profit = Math.round(revLTV * margin / 100) * 100;
    const cac = totalSpend / (purchases || 1);
    return {
      leads: Math.round(leads),
      appts: Math.round(appts),
      shows: Math.round(shows),
      purchases: Math.round(purchases),
      revFirst,
      revGoBack,
      revLTV,
      profit,
      cpl: totalSpend / (leads || 1),
      cpa: totalSpend / (appts || 1),
      cps: totalSpend / (shows || 1),
      cac,
      roasLTV: revLTV / totalSpend,
      ltvToCac: industry.ltv5yr / cac
    };
  }, [industry, totalSpend, toggleValue, includeReferrals]);
  const cacColor = m.ltvToCac < 3 ? 'text-red-500' : m.ltvToCac < 10 ? 'text-[#D4A944]' : m.ltvToCac < 20 ? 'text-[#8FBC8F]' : 'text-[#C9A961]';
  const funnel = [{
    label: 'Leads',
    val: m.leads,
    color: '#555'
  }, {
    label: 'Appts',
    val: m.appts,
    color: '#777'
  }, {
    label: 'Shows',
    val: m.shows,
    color: '#999'
  }, {
    label: 'Purchases',
    val: m.purchases,
    color: '#C9A961'
  }];
  return <section id="traffic-sources" className="bg-[#0A0A0A] text-[#F5F1EA] py-12 px-4 md:px-6 overflow-x-hidden" data-id="element-624" data-mp="19ztub5wntlsu">
      <div className="max-w-7xl mx-auto" data-id="element-625" data-mp="1nri5n6z3468r">
        {/* Row 1: Title + Industry + Spend */}
        <div className="flex flex-col lg:flex-row lg:items-end gap-4 mb-6" data-id="element-626" data-mp="1iwudmy4vgtpf">
          <div className="flex-shrink-0" data-id="element-627" data-mp="1xma227gxkn1y">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight" data-id="element-628" data-mp="1soqo78o5acaj">
              Attribution Calculator
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-629" data-mp="26orrsvvsnxtp">
              See what your ad spend should produce.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-4 flex-1" data-id="element-630" data-mp="21q80efyfltty">
            {/* Industry */}
            <div className="relative" data-id="element-631" data-mp="caqsyb08vatm">
              <select value={industryKey} onChange={e => setIndustryKey(e.target.value)} className="appearance-none bg-[#1A1A1A] border border-[#333] rounded-lg py-2 pl-3 pr-8 text-sm text-[#F5F1EA] focus:outline-none focus:border-[#C9A961]" data-id="element-632" data-mp="7bnkegl22mgw">
                {Object.entries(industries).map(([k, v]) => <option key={k} value={k} data-id="element-633" data-mp="2a4cqvyoaamtt">
                    {v.name}
                  </option>)}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-634" data-mp="207q4v6k81pq7" />
            </div>
            {/* Spend slider */}
            <div className="flex-1 min-w-[200px]" data-id="element-635" data-mp="1r7l1d7i5fbhb">
              <div className="flex justify-between text-xs text-gray-400 mb-1" data-id="element-636" data-mp="1r8wc5ra0hjhx">
                <span data-id="element-637" data-mp="1va7vxg67fai7">Monthly Ad Spend</span>
                <span className="text-[#C9A961] font-bold text-sm" data-id="element-638" data-mp="1vb9a3tiiod45">
                  {fmt(totalSpend)}
                </span>
              </div>
              <input type="range" min="1000" max="100000" step="1000" value={totalSpend} onChange={e => setTotalSpend(Number(e.target.value))} className="w-full h-1.5 bg-[#222] rounded-lg appearance-none cursor-pointer accent-[#C9A961]" data-id="element-639" data-mp="3pnh8xhxyle57" />
            </div>
            {/* Growth Lever inline */}
            <div className="flex items-center gap-2 bg-[#111] border border-[#C9A961]/30 rounded-lg px-3 py-2" data-id="element-640" data-mp="ogabyc9e4fet">
              <span className="bg-[#C9A961] text-black text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5" data-id="element-641" data-mp="1l4gd2p73spoc">
                <TrendingUp className="w-2.5 h-2.5" data-id="element-642" data-mp="1ccxyujtpwjxw" /> Lever
              </span>
              {industry.toggle.isToggle ? <label className="flex items-center cursor-pointer gap-2" data-id="element-643" data-mp="17ecuq9bchg46">
                  <span className="text-xs text-gray-300 whitespace-nowrap" title={industry.toggle.description} data-id="element-644" data-mp="24t8p4lhuxu4i">
                    {industry.toggle.label}
                  </span>
                  <div className="relative" data-id="element-645" data-mp="27eospmsz30tt">
                    <input type="checkbox" className="sr-only" checked={toggleValue === 1} onChange={e => setToggleValue(e.target.checked ? 1 : 0)} data-id="element-646" data-mp="27fzy1r22ggn1" />
                    <div className={`w-9 h-5 rounded-full transition-colors ${toggleValue === 1 ? 'bg-[#C9A961]' : 'bg-[#333]'}`} data-id="element-647" data-mp="2le7n7csv2dnn" />
                    <div className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${toggleValue === 1 ? 'translate-x-4' : ''}`} data-id="element-648" data-mp="2vau821l6t4po" />
                  </div>
                </label> : <div className="flex items-center gap-2" data-id="element-649" data-mp="3930olos95dpn">
                  <span className="text-xs text-gray-300 whitespace-nowrap cursor-help" title={industry.toggle.description} data-id="element-650" data-mp="394ljjfihayo1">
                    {industry.toggle.label}
                  </span>
                  <input type="range" min="0" max={industry.toggle.stretch} step="1" value={toggleValue} onChange={e => setToggleValue(Number(e.target.value))} className="w-20 h-1 bg-[#222] rounded appearance-none cursor-pointer accent-[#C9A961]" data-id="element-651" data-mp="3ue7jjan4p5fi" />
                  <span className="text-xs font-bold text-white w-8" data-id="element-652" data-mp="3bnzlv827776p">
                    {toggleValue}%
                  </span>
                </div>}
            </div>
            {/* Referral toggle */}
            <label className="flex items-center gap-2 cursor-pointer bg-[#111] border border-[#1E1E1E] rounded-lg px-3 py-2" data-id="element-653" data-mp="3lkm5jtisfs75">
              <span className="text-xs text-gray-400 whitespace-nowrap" data-id="element-654" data-mp="2xu8d41wajrc1">
                Referrals
              </span>
              <div className="relative" data-id="element-655" data-mp="2sv5g94qgsrf5">
                <input type="checkbox" className="sr-only" checked={includeReferrals} onChange={e => setIncludeReferrals(e.target.checked)} data-id="element-656" data-mp="18enstwe1o16o" />
                <div className={`w-8 h-4.5 rounded-full transition-colors ${includeReferrals ? 'bg-[#C9A961]' : 'bg-[#333]'}`} style={{
                width: 32,
                height: 18
              }} data-id="element-657" data-mp="yikuz2q86opj" />
                <div className={`absolute left-0.5 top-0.5 bg-white w-3.5 h-3.5 rounded-full transition-transform`} style={{
                width: 14,
                height: 14,
                transform: includeReferrals ? 'translateX(14px)' : 'translateX(0)'
              }} data-id="element-658" data-mp="1w3gwkf0wrdbu" />
              </div>
            </label>
          </div>
        </div>

        {/* Revenue row - consolidated inline */}
        <div className="flex flex-col md:flex-row items-stretch gap-3 mb-4 bg-[#111] border border-[#1E1E1E] rounded-xl p-4" data-id="element-659" data-mp="snl4wh4yahqf">
          <div className="flex-1 text-center md:text-left" data-id="element-660" data-mp="xnh4832pdkzn">
            <p className="text-[10px] uppercase tracking-widest text-gray-500" data-id="element-661" data-mp="jswu6bglw7u8">
              First Visit
            </p>
            <div className="text-xl font-bold text-gray-400" data-id="element-662" data-mp="ntqllet9c4rv">
              <AnimatedNumber value={m.revFirst} format="currency" data-id="element-663" data-mp="9virdenlvhke" />
            </div>
          </div>
          <div className="hidden md:block w-px bg-[#2A2A2A]" data-id="element-664" data-mp="ev54lo3foxt7" />
          <div className="flex-1 text-center md:text-left" data-id="element-665" data-mp="v3tzgxdac0j">
            <p className="text-[10px] uppercase tracking-widest text-gray-400" data-id="element-666" data-mp="woo55frrsxv">
              12-Mo Go-Back
            </p>
            <div className="text-xl font-bold text-[#F5F1EA]" data-id="element-667" data-mp="4ys529l7jpqj">
              <AnimatedNumber value={m.revGoBack} format="currency" data-id="element-668" data-mp="1vag7iwnwhr6i" />
            </div>
          </div>
          <div className="hidden md:block w-px bg-[#2A2A2A]" data-id="element-669" data-mp="1z9fawctzhfvu" />
          <div className="flex-1 text-center md:text-left" data-id="element-670" data-mp="3js9a1glkjk8x">
            <p className="text-[10px] uppercase tracking-widest text-[#C9A961]" data-id="element-671" data-mp="3jqofvs3623bl">
              5-Year LTV
            </p>
            <div className="text-2xl font-bold text-[#C9A961]" data-id="element-672" data-mp="3tikf56gznzhe">
              <AnimatedNumber value={m.revLTV} format="currency" data-id="element-673" data-mp="3tjvhr2yp716x" />
            </div>
          </div>
        </div>

        {/* Row: Funnel + Metrics side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4" data-id="element-674" data-mp="7akslyd7xxem">
          {/* Compact Funnel */}
          <div className="bg-[#111] border border-[#1E1E1E] rounded-xl p-4" data-id="element-675" data-mp="2b7v8joq17xs">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2" data-id="element-676" data-mp="gbsibs1o5px3">
              Sales Funnel
            </p>
            <div className="space-y-1.5" data-id="element-677" data-mp="be8ymcm7ckxr">
              {funnel.map((s, i) => <div key={s.label} className="flex items-center gap-2" data-id="element-678" data-mp="2i4gmc3m50hdx" data-mpi={i}>
                  <span className="text-[11px] text-gray-400 w-16 shrink-0" data-id="element-679" data-mp="2d4khk25m8lxb">
                    {s.label}
                  </span>
                  <div className="flex-1 bg-[#1A1A1A] h-2 rounded overflow-hidden" data-id="element-680" data-mp="h27kju0d9xbz">
                    <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${Math.max(3, s.val / (m.leads || 1) * 100)}%`
                }} transition={{
                  duration: 0.6,
                  ease: 'easeOut'
                }} className="h-full rounded" style={{
                  backgroundColor: s.color
                }} data-id="element-681" data-mp="lzharbvkyd0v" />
                  </div>
                  <span className="text-[11px] font-bold text-white w-10 text-right" data-id="element-682" data-mp="1enwlhm7oi96e">
                    <AnimatedNumber value={s.val} data-id="element-683" data-mp="1ir1kvfwgpuqt" />
                  </span>
                </div>)}
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-3 gap-2" data-id="element-684" data-mp="387ng7pvq67dw">
            {[{
            label: 'CPL',
            val: m.cpl,
            fmt: 'currency' as const
          }, {
            label: 'Cost/Appt',
            val: m.cpa,
            fmt: 'currency' as const
          }, {
            label: 'CAC',
            val: m.cac,
            fmt: 'currency' as const
          }, {
            label: 'ROAS (LTV)',
            val: m.roasLTV,
            fmt: 'number' as const,
            suffix: 'x'
          }, {
            label: 'Cost/Show',
            val: m.cps,
            fmt: 'currency' as const
          }, {
            label: 'LTV:CAC',
            val: m.ltvToCac,
            fmt: 'ratio' as const,
            highlight: true
          }].map(card => <div key={card.label} className={`rounded-lg p-3 relative group ${card.highlight ? 'bg-[#1A1A1A] border border-[#333] cursor-help' : 'bg-[#111] border border-[#1E1E1E]'}`} data-id="element-685" data-mp="9o6phxttipmb">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1" data-id="element-686" data-mp="3gfzyfojzeedn">
                  {card.label}
                </p>
                <div className={`text-lg font-bold ${card.highlight ? cacColor : 'text-white'}`} data-id="element-687" data-mp="3lekztrayz43o">
                  {card.fmt === 'number' ? <>
                      <AnimatedNumber value={card.val} data-id="element-688" data-mp="gzbdc75x0g4j" />
                      {card.suffix}
                    </> : <AnimatedNumber value={card.val} format={card.fmt} data-id="element-689" data-mp="87jaahj8099z" />}
                </div>
                {card.highlight && <div className="absolute bottom-full right-0 mb-2 w-72 bg-[#1A1A1A] border border-[#333] rounded-xl p-4 shadow-2xl opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-200 z-50" data-id="element-690" data-mp="vw2hetqjag0h">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#C9A961] mb-3" data-id="element-691" data-mp="1623r1aii7c13">
                      LTV:CAC Cheat Sheet
                    </p>
                    <div className="space-y-2 text-[11px]" data-id="element-692" data-mp="1etm61i3prlsl">
                      <div className="flex gap-2" data-id="element-693" data-mp="1eu5px4cxck99">
                        <span className="text-red-500 font-bold shrink-0 w-14" data-id="element-694" data-mp="r3rrmw3qxp68">
                          Below 1:1
                        </span>
                        <span className="text-gray-400" data-id="element-695" data-mp="n40xcif0gqwy">
                          You're losing money on every client. Stop spending
                          immediately.
                        </span>
                      </div>
                      <div className="flex gap-2" data-id="element-696" data-mp="w5h6kmu3amzl">
                        <span className="text-red-400 font-bold shrink-0 w-14" data-id="element-697" data-mp="2cgbb60tpqeio">
                          1:1 - 3:1
                        </span>
                        <span className="text-gray-400" data-id="element-698" data-mp="27knmrnn21pe8">
                          Surviving but not building wealth. Most "profit" goes
                          back into ads.
                        </span>
                      </div>
                      <div className="flex gap-2" data-id="element-699" data-mp="2oje3t6k5xif1">
                        <span className="text-[#D4A944] font-bold shrink-0 w-14" data-id="element-700" data-mp="2ohkrpbqs65zn">
                          3:1
                        </span>
                        <span className="text-gray-400" data-id="element-701" data-mp="1vvrmywlxcha8">
                          Industry-accepted healthy benchmark. You're
                          sustainable.
                        </span>
                      </div>
                      <div className="flex gap-2" data-id="element-702" data-mp="25se9dptwfged">
                        <span className="text-[#8FBC8F] font-bold shrink-0 w-14" data-id="element-703" data-mp="2ajwb4g3qxl0e">
                          3:1 - 10:1
                        </span>
                        <span className="text-gray-400" data-id="element-704" data-mp="1d3z6g1o1koje">
                          You're profitable. Marketing is working.
                        </span>
                      </div>
                      <div className="flex gap-2" data-id="element-705" data-mp="1aj15ceq6msag">
                        <span className="text-[#8FBC8F] font-bold shrink-0 w-14" data-id="element-706" data-mp="1ahq2ch4k9oke">
                          Above 10:1
                        </span>
                        <span className="text-gray-400" data-id="element-707" data-mp="10kv7q9bes8be">
                          You should be spending MORE on ads. You're
                          under-investing.
                        </span>
                      </div>
                      <div className="flex gap-2" data-id="element-708" data-mp="rmj85hske06t">
                        <span className="text-[#C9A961] font-bold shrink-0 w-14" data-id="element-709" data-mp="mtyi8rcs478k">
                          20:1+
                        </span>
                        <span className="text-gray-400" data-id="element-710" data-mp="hvohm18acefk">
                          Exceptional retention or under-spending on growth.
                          Probably both.
                        </span>
                      </div>
                    </div>
                  </div>}
              </div>)}
          </div>
        </div>

        {/* Annual Projection Strip */}
        <div className="bg-gradient-to-r from-[#1A150A] via-[#1E1A0E] to-[#1A150A] border border-[#C9A961]/20 rounded-lg px-4 py-3 text-center" data-id="element-711" data-mp="1rt3dtfwl3s6e">
          <p className="text-xs md:text-sm text-[#F5F1EA]" data-id="element-712" data-mp="1hunmpcxk4iww">
            Over 12 months:{' '}
            <strong className="text-[#C9A961]" data-id="element-713" data-mp="1msyyht7ex3hr">
              <AnimatedNumber value={m.revGoBack * 12} format="currency" data-id="element-714" data-mp="1mrnubsa21tq4" />
            </strong>{' '}
            go-back revenue |{' '}
            <strong className="text-[#8FBC8F]" data-id="element-715" data-mp="1dsk8whnf128g">
              <AnimatedNumber value={m.profit} format="currency" data-id="element-716" data-mp="1dtc802g81i3h" />
            </strong>{' '}
            5yr profit |{' '}
            <strong className="text-white" data-id="element-717" data-mp="z5hckeatwzmp">
              <AnimatedNumber value={m.purchases * 12} data-id="element-718" data-mp="1427rgyawdp6j" />
            </strong>{' '}
            new clients
          </p>
        </div>

        {/* Footer */}
        <p className="text-[10px] text-gray-600 text-center mt-4 max-w-3xl mx-auto" data-id="element-719" data-mp="q3rj72xdzqma">
          Projections based on industry benchmarks from AmSpa, ServiceTitan,
          FieldRoutes, Profitability Partners, FieldEdge, WhatConverts, and 200+
          P&L reviews. Actual results depend on creative quality, offer quality,
          and local market.
        </p>
      </div>
    </section>;
}